import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel
} from '@mui/material';

// Define role IDs based on database values
const ROLE_ID_TEAM_MEMBER = 3;
const ROLE_ID_TEAM_ADMIN = 2;

const InviteMemberDialog = ({ open, onClose, onSendInvite, teams = [], companyName = '', companyId = null }) => {
  const [email, setEmail] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState(''); // For existing teams
  const [newTeamName, setNewTeamName] = useState(''); // For creating a new team
  const [role, setRole] = useState(ROLE_ID_TEAM_MEMBER); // Initialize with numeric role ID
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [canCreateNewTeam, setCanCreateNewTeam] = useState(false); // User can toggle to create new team

  // Reset form when dialog opens/closes or teams change
  useEffect(() => {
    if (open) {
      setEmail('');
      setSelectedTeamId(teams.length > 0 ? teams[0].team_id : ''); // Default to first team or empty
      setNewTeamName('');
      setRole(ROLE_ID_TEAM_MEMBER); // Reset to default numeric role ID
      setError('');
      setIsSubmitting(false);
      setCanCreateNewTeam(teams.length === 0); // Default to create new team if no existing teams
    }
  }, [open, teams]);

  const handleSubmit = async () => {
    if (!email.trim()) {
      setError('Email address is required.');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!canCreateNewTeam && !selectedTeamId) {
        setError('Please select a team.');
        return;
    }
    if (canCreateNewTeam && !newTeamName.trim()) {
        setError('Please enter a name for the new team.');
        return;
    }

    setError('');

    // Preflight: subscription feature flag: team collaboration must be enabled
    try {
      if (companyId) {
        const { data: subFeature } = await supabase
          .from('company_subscriptions')
          .select('team_collaboration_enabled, status')
          .eq('company_id', companyId)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (subFeature && subFeature.team_collaboration_enabled === false) {
          setError('Your plan does not include team collaboration.');
          return;
        }
      }
    } catch (e) {
      console.warn('Failed to check team collaboration feature flag:', e);
    }

    // Preflight: if assigning to an existing team, check server-side limit via RPC
    if (!canCreateNewTeam && selectedTeamId) {
      try {
        const { data, error } = await supabase.rpc('can_add_team_member', { p_team_id: selectedTeamId });
        if (error) {
          setError(error.message || 'Failed to check team member limit.');
          return;
        }
        if (data === false) {
          setError('Your company has reached the team member limit.');
          return;
        }
      } catch (e) {
        console.error('Preflight can_add_team_member RPC failed:', e);
        setError('Could not verify team member limit. Please try again.');
        return;
      }
    }

    // Preflight: if creating a new team, check company-level limit via RPC if companyId is provided
    if (canCreateNewTeam && companyId) {
      try {
        const { data, error } = await supabase.rpc('can_add_company_member', { p_company_id: companyId });
        if (error) {
          setError(error.message || 'Failed to check company member limit.');
          return;
        }
        if (data === false) {
          setError('Your company has reached the team member limit.');
          return;
        }
      } catch (e) {
        console.error('Preflight can_add_company_member RPC failed:', e);
        setError('Could not verify company member limit. Please try again.');
        return;
      }
    }

    setIsSubmitting(true);

    const inviteData = {
      email: email.trim(),
      assigned_role_id: role,
      team_id: canCreateNewTeam ? null : selectedTeamId,
      new_team_name: canCreateNewTeam ? newTeamName.trim() : null,
    };
    
    try {
      // onSendInvite is expected to be an async function that might throw
      // an error object, potentially a FunctionsHttpError from supabase-js.
      await onSendInvite(inviteData);
      
      // If onSendInvite resolves without error, it implies success in this context.
      // The parent component calling this dialog might handle its own success messages.
      setIsSubmitting(false);
      onClose(); // Close dialog on success

    } catch (err) {
      setIsSubmitting(false);
      let displayError = 'Failed to send invitation. Please try again.';
      console.error('Error during onSendInvite call in InviteMemberDialog:', err);

      // Check if it's a Supabase FunctionsHttpError and try to parse JSON
      // (err.context might not exist on all error types, hence the checks)
      if (err && err.context && typeof err.context.json === 'function') {
        try {
          // For FunctionsHttpError, err.context.json() returns a Promise
          const backendError = await err.context.json(); 
          console.log('Parsed backend error from FunctionsHttpError:', backendError);
          displayError = backendError.details || backendError.error || displayError;
        } catch (parseError) {
          console.error('Could not parse JSON from FunctionsHttpError context:', parseError);
          displayError = err.message || displayError;
        }
      } else if (err && err.message) {
        displayError = err.message;
      }
      setError(displayError);
    }
  };

  const handleCloseDialog = () => {
    if (isSubmitting) return; // Prevent closing while submitting
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
      <DialogTitle>Invite New Member{companyName ? ` to ${companyName}` : ''}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          autoFocus
          margin="dense"
          id="email"
          label="Email Address"
          type="email"
          fullWidth
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isSubmitting}
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={canCreateNewTeam}
              onChange={(e) => {
                setCanCreateNewTeam(e.target.checked);
                if (e.target.checked) {
                  setSelectedTeamId(''); // Clear selected team if creating new
                } else if (teams.length > 0) {
                  setSelectedTeamId(teams[0].team_id); // Default to first existing team
                }
              }}
              disabled={isSubmitting}
            />
          }
          label="Create a new team for this member"
          sx={{ mb: 1 }}
        />

        {canCreateNewTeam ? (
          <TextField
            margin="dense"
            id="newTeamName"
            label="New Team Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            disabled={isSubmitting}
            sx={{ mb: 2 }}
          />
        ) : (
          <FormControl fullWidth margin="dense" disabled={isSubmitting || teams.length === 0} sx={{ mb: 2 }}>
            <InputLabel id="team-select-label">Assign to Team</InputLabel>
            <Select
              labelId="team-select-label"
              id="team"
              value={selectedTeamId}
              label="Assign to Team"
              onChange={(e) => setSelectedTeamId(e.target.value)}
            >
              {teams.length === 0 && <MenuItem disabled value="">No teams available</MenuItem>}
              {teams.map((team) => (
                <MenuItem key={team.team_id} value={team.team_id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl fullWidth margin="dense" disabled={isSubmitting}>
          <InputLabel id="role-select-label">Role</InputLabel>
          <Select
            labelId="role-select-label"
            id="role"
            value={role}
            label="Role"
            onChange={(e) => setRole(Number(e.target.value))}
          >
            <MenuItem value={ROLE_ID_TEAM_MEMBER}>Member</MenuItem>
            <MenuItem value={ROLE_ID_TEAM_ADMIN}>Admin (Team)</MenuItem>
            {/* Add other roles as needed, using their numeric role_id as value */}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={handleCloseDialog} color="inherit" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting}>
          {isSubmitting ? <CircularProgress size={24} /> : 'Send Invitation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteMemberDialog; 